import moment from 'moment';
import { isObject, isPlainObject, template, uniqBy, uniq, groupBy, has, get, merge, isArray, map, each } from 'lodash';
import qs from 'qs';

/**
 * 代码来源于amis
 * https://github.com/baidu/amis
 */
// 方便取值的时候能够把上层的取到，但是获取的时候不会全部把所有的数据获取到。
function createObject(superProps, props, properties) {
    if (superProps && Object.isFrozen(superProps)) {
        superProps = cloneObject(superProps);
    }
    const obj = superProps
        ? Object.create(superProps, {
            ...properties,
            __super: {
                value: superProps,
                writable: false,
                enumerable: false,
            },
        })
        : Object.create(Object.prototype, properties);
    props &&
        isObject(props) &&
        Object.keys(props).forEach((key) => (obj[key] = props[key]));
    return obj;
}
function object2formData(data, options = {
    arrayFormat: 'indices',
    encodeValuesOnly: true,
}, fd = new FormData()) {
    const fileObjects = [];
    const others = {};
    Object.keys(data).forEach((key) => {
        const value = data[key];
        if (value instanceof File) {
            fileObjects.push([key, value]);
        }
        else if (Array.isArray(value) &&
            value.length &&
            value[0] instanceof File) {
            value.forEach((value) => fileObjects.push([`${key}[]`, value]));
        }
        else {
            others[key] = value;
        }
    });
    // 因为 key 的格式太多了，偷个懒，用 qs 来处理吧。
    qsstringify(others, options)
        .split('&')
        .forEach((item) => {
        const parts = item.split('=');
        // form-data/multipart 是不需要 encode 值的。
        parts[0] && fd.append(parts[0], decodeURIComponent(parts[1]));
    });
    // Note: File类型字段放在后面，可以支持第三方云存储鉴权
    fileObjects.forEach((fileObject) => fd.append(fileObject[0], fileObject[1], fileObject[1].name));
    return fd;
}
// 只判断一层, 如果层级很深，form-data 也不好表达。
function hasFile(object) {
    return Object.keys(object).some((key) => {
        const value = object[key];
        return (value instanceof File ||
            (Array.isArray(value) && value.length && value[0] instanceof File));
    });
}
function rmUndefined(obj) {
    const newObj = {};
    if (typeof obj !== 'object') {
        return obj;
    }
    const keys = Object.keys(obj);
    keys.forEach((key) => {
        if (obj[key] !== undefined) {
            newObj[key] = obj[key];
        }
    });
    return newObj;
}
function isObjectShallowModified(prev, next, strictMode = true, ignoreUndefined = false) {
    if (Array.isArray(prev) && Array.isArray(next)) {
        return prev.length !== next.length
            ? true
            : prev.some((prev, index) => isObjectShallowModified(prev, next[index], strictMode, ignoreUndefined));
    }
    else if (null == prev ||
        null == next ||
        !isObject(prev) ||
        !isObject(next)) {
        return strictMode ? prev !== next : prev != next;
    }
    if (ignoreUndefined) {
        prev = rmUndefined(prev);
        next = rmUndefined(next);
    }
    const keys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    if (keys.length !== nextKeys.length ||
        keys.sort().join(',') !== nextKeys.sort().join(',')) {
        return true;
    }
    for (let i = keys.length - 1; i >= 0; i--) {
        const key = keys[i];
        if (strictMode
            ? next[key] !== prev[key]
            : isObjectShallowModified(next[key], prev[key], false, ignoreUndefined)) {
            return true;
        }
    }
    return false;
}
function cloneObject(target, persistOwnProps = true) {
    const obj = target && target.__super
        ? Object.create(target.__super, {
            __super: {
                value: target.__super,
                writable: false,
                enumerable: false,
            },
        })
        : Object.create(Object.prototype);
    persistOwnProps &&
        target &&
        Object.keys(target).forEach((key) => (obj[key] = target[key]));
    return obj;
}
function setVariable(data, key, value) {
    data = data || {};
    if (key in data) {
        data[key] = value;
        return;
    }
    const parts = keyToPath(key);
    const last = parts.pop();
    while (parts.length) {
        const key = parts.shift();
        if (isPlainObject(data[key])) {
            data = data[key] = {
                ...data[key],
            };
        }
        else if (Array.isArray(data[key])) {
            data[key] = data[key].concat();
            data = data[key];
        }
        else if (data[key]) {
            // throw new Error(`目标路径不是纯对象，不能覆盖`);
            // 强行转成对象
            data[key] = {};
            data = data[key];
        }
        else {
            data[key] = {};
            data = data[key];
        }
    }
    data[last] = value;
}
function string2regExp(value, caseSensitive = false) {
    if (typeof value !== 'string') {
        throw new TypeError('Expected a string');
    }
    return new RegExp(value.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&').replace(/-/g, '\\x2d'), !caseSensitive ? 'i' : '');
}
function qsstringify(data, options = {
    arrayFormat: 'indices',
    encodeValuesOnly: true,
}) {
    return qs.stringify(data, options);
}
/**
 * 将例如像 a.b.c 或 a[1].b 的字符串转换为路径数组
 *
 * @param string 要转换的字符串
 */
const keyToPath = (string) => {
    const result = [];
    if (string.charCodeAt(0) === '.'.charCodeAt(0)) {
        result.push('');
    }
    string.replace(new RegExp('[^.[\\]]+|\\[(?:([^"\'][^[]*)|(["\'])((?:(?!\\2)[^\\\\]|\\\\.)*?)\\2)\\]|(?=(?:\\.|\\[\\])(?:\\.|\\[\\]|$))', 'g'), (match, expression, quote, subString) => {
        let key = match;
        if (quote) {
            key = subString.replace(/\\(\\)?/g, '$1');
        }
        else if (expression) {
            key = expression.trim();
        }
        result.push(key);
        return '';
    });
    return result;
};

/**
 * 代码来源于amis
 * https://github.com/baidu/amis
 */
const imports = {
    default: undefined,
    moment: moment,
    countDown: (end) => {
        if (!end) {
            return '--';
        }
        const date = new Date(parseInt(end, 10) * 1000);
        const now = Date.now();
        if (date.getTime() < now) {
            return '已结束';
        }
        return Math.ceil((date.getTime() - now) / (1000 * 60 * 60 * 24)) + '天';
    },
    formatDate: (value, format = 'LLL', inputFormat = '') => moment(value, inputFormat).format(format),
};
// 缓存一下提升性能
const EVAL_CACHE = {};
function lodashCompile(str, data) {
    try {
        const filters = getFilters();
        const finnalImports = {
            ...filters,
            formatTimeStamp: filters.date,
            formatNumber: filters.number,
            defaultValue: filters.defaut,
            ...imports,
        };
        delete finnalImports.default; // default 是个关键字，不能 imports 到 lodash 里面去。
        const fn = EVAL_CACHE[str] ||
            (EVAL_CACHE[str] = template(str, {
                imports: finnalImports,
                variable: 'data',
            }));
        return fn.call(data, data);
    }
    catch (e) {
        return `<span class="text-danger">${e.message}</span>`;
    }
}
function registerLodash() {
    return {
        name: 'lodash',
        test: (str) => !!str.includes('<%'),
        compile: (str, data) => lodashCompile(str, data),
    };
}

const enginers = {};
function registerTplEnginer(name, enginer) {
    enginers[name] = enginer;
}
function filter(tpl, data = {}, ...rest) {
    if (!tpl || typeof tpl !== 'string') {
        return '';
    }
    for (const k of Object.keys(enginers)) {
        const enginer = enginers[k];
        if (enginer.test(tpl)) {
            return enginer.compile(tpl, data, ...rest);
        }
    }
    return tpl;
}
// 缓存一下提升性能
const EVAL_CACHE$1 = {};
// 缓存一下提升性能
const FUN_EVAL_CACHE = new WeakMap();
let customEvalExpressionFn;
function setCustomEvalExpression(fn) {
    customEvalExpressionFn = fn;
}
// 几乎所有的 visibleOn requiredOn 都是通过这个方法判断出来结果，很粗暴也存在风险，建议自己实现。
// 如果想自己实现，请通过 setCustomEvalExpression 来替换。
function evalExpression(expression, data) {
    if (typeof customEvalExpressionFn === 'function') {
        return customEvalExpressionFn(expression, data);
    }
    if (!expression || typeof expression !== 'string') {
        return false;
    }
    /* jshint evil:true */
    try {
        let debug = false;
        const idx = expression.indexOf('debugger');
        if (~idx) {
            debug = true;
            expression = expression.replace(/debugger;?/, '');
        }
        let fn;
        if (expression in EVAL_CACHE$1) {
            fn = EVAL_CACHE$1[expression];
        }
        else {
            fn = new Function('data', 'utils', `with(data) {${debug ? 'debugger;' : ''}return !!(${expression});}`);
            EVAL_CACHE$1[expression] = fn;
        }
        data = data || {};
        return fn.call(data, data, getFilters());
    }
    catch (e) {
        console.warn(expression, e);
        return false;
    }
}
let customEvalJsFn;
function setCustomEvalJs(fn) {
    customEvalJsFn = fn;
}
// 这个主要用在 formula 里面，用来动态的改变某个值。也很粗暴，建议自己实现。
// 如果想自己实现，请通过 setCustomEvalJs 来替换。
function evalJS(js, data) {
    if (typeof customEvalJsFn === 'function') {
        return customEvalJsFn(js, data);
    }
    /* jshint evil:true */
    try {
        const fn = new Function('data', 'utils', `with(data) {${/^\s*return\b/.test(js) ? '' : 'return '}${js};}`);
        data = data || {};
        return fn.call(data, data, getFilters());
    }
    catch (e) {
        console.warn(js, e);
        return null;
    }
}
const defaultData = {};
function createStr2Function(js, data = defaultData) {
    try {
        let fun;
        const tempFunMap = FUN_EVAL_CACHE.get(data);
        if (tempFunMap && tempFunMap[js])
            return tempFunMap[js];
        if (js.includes('function')) {
            fun = new Function(`return ${js}`)();
        }
        else {
            fun = new Function(js);
        }
        const funResult = fun.bind(data);
        if (tempFunMap) {
            FUN_EVAL_CACHE.set(data, { ...tempFunMap, [js]: funResult });
        }
        else {
            FUN_EVAL_CACHE.set(data, { [js]: funResult });
        }
        return funResult;
    }
    catch (e) {
        console.warn(js, e);
        return undefined;
    }
}
[registerBuiltin, registerLodash].forEach((fn) => {
    const info = fn();
    registerTplEnginer(info.name, {
        test: info.test,
        compile: info.compile,
    });
});

/**
 * 代码来源于amis
 * https://github.com/baidu/amis
 */
const UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
const prettyBytes = (num) => {
    if (!Number.isFinite(num)) {
        throw new TypeError(`Expected a finite number, got ${typeof num}: ${num}`);
    }
    const neg = num < 0;
    if (neg) {
        num = -num;
    }
    if (num < 1) {
        return (neg ? '-' : '') + num + ' B';
    }
    const exponent = Math.min(Math.floor(Math.log(num) / Math.log(1000)), UNITS.length - 1);
    const numStr = Number((num / Math.pow(1000, exponent)).toPrecision(3));
    const unit = UNITS[exponent];
    return (neg ? '-' : '') + numStr + ' ' + unit;
};
const entityMap = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '/': '&#x2F;',
};
const escapeHtml = (str) => String(str).replace(/[&<>"'\/]/g, function (s) {
    return entityMap[s];
});
function formatDuration(value) {
    const unit = ['秒', '分', '时', '天', '月', '季', '年'];
    const steps = [1, 60, 3600, 86400, 2592000, 7776000, 31104000];
    let len = steps.length;
    const parts = [];
    while (len--) {
        if (steps[len] && value >= steps[len]) {
            parts.push(Math.floor(value / steps[len]) + unit[len]);
            value %= steps[len];
        }
        else if (len === 0 && value) {
            parts.push((value.toFixed ? value.toFixed(2) : '0') + unit[0]);
        }
    }
    return parts.join('');
}
function makeSorter(key, method, order) {
    return function (a, b) {
        if (!a || !b) {
            return 0;
        }
        const va = resolveVariable(key, a);
        const vb = resolveVariable(key, b);
        let result = 0;
        if (method === 'numerical') {
            result = (parseFloat(va) || 0) - (parseFloat(vb) || 0);
        }
        else {
            result = String(va).localeCompare(String(vb));
        }
        return result * (order === 'desc' ? -1 : 1);
    };
}
const timeUnitMap = {
    year: 'Y',
    month: 'M',
    week: 'w',
    weekday: 'W',
    day: 'd',
    hour: 'h',
    minute: 'm',
    min: 'm',
    second: 's',
    millisecond: 'ms',
};
const relativeValueRe = /^(.+)?(\+|-)(\d+)(minute|min|hour|day|week|month|year|weekday|second|millisecond)s?$/i;
const filterDate = (value, data = {}, format = 'X', utc = false) => {
    let m;
    const mm = utc ? moment.utc : moment;
    if (typeof value === 'string') {
        value = value.trim();
    }
    value = tokenize(value, data);
    if (value && typeof value === 'string' && (m = relativeValueRe.exec(value))) {
        const date = new Date();
        const step = parseInt(m[3], 10);
        const from = m[1]
            ? filterDate(m[1], data, format, utc)
            : mm(/(minute|min|hour|second)s?/.test(m[4])
                ? [
                    date.getFullYear(),
                    date.getMonth(),
                    date.getDate(),
                    date.getHours(),
                    date.getMinutes(),
                    date.getSeconds(),
                ]
                : [date.getFullYear(), date.getMonth(), date.getDate()]);
        return m[2] === '-'
            ? from.subtract(step, timeUnitMap[m[4]])
            : from.add(step, timeUnitMap[m[4]]);
        //   return from[m[2] === '-' ? 'subtract' : 'add'](step, mapping[m[4]] || m[4]);
    }
    else if (value === 'now') {
        return mm();
    }
    else if (value === 'today') {
        const date = new Date();
        return mm([date.getFullYear(), date.getMonth(), date.getDate()]);
    }
    else {
        return mm(value, format);
    }
};
function parseDuration(str) {
    const matches = /^((?:\-|\+)?(?:\d*\.)?\d+)(minute|min|hour|day|week|month|year|weekday|second|millisecond)s?$/.exec(str);
    if (matches) {
        const duration = moment.duration(parseFloat(matches[1]), matches[2]);
        if (moment.isDuration(duration)) {
            return duration;
        }
    }
    return;
}
const filters = {
    html: (input) => escapeHtml(input),
    json: (input, tabSize = 2) => tabSize
        ? JSON.stringify(input, null, parseInt(tabSize, 10))
        : JSON.stringify(input),
    toJson: (input) => {
        let ret;
        try {
            ret = JSON.parse(input);
        }
        catch (e) {
            ret = null;
        }
        return ret;
    },
    toInt: (input) => (typeof input === 'string' ? parseInt(input, 10) : input),
    toFloat: (input) => (typeof input === 'string' ? parseFloat(input) : input),
    raw: (input) => input,
    now: () => new Date(),
    toDate: (input, inputFormat = '') => {
        const data = moment(input, inputFormat);
        data.add();
        return data.isValid() ? data.toDate() : undefined;
    },
    dateModify: (input, modifier = 'add', amount = 0, unit = 'days') => {
        if (!(input instanceof Date)) {
            input = new Date();
        }
        if (modifier === 'endOf' || modifier === 'startOf') {
            return moment(input)[modifier === 'endOf' ? 'endOf' : 'startOf'](amount || 'day')
                .toDate();
        }
        return moment(input)[modifier === 'add' ? 'add' : 'subtract'](parseInt(amount, 10) || 0, unit)
            .toDate();
    },
    date: (input, format = 'LLL', inputFormat = 'X') => moment(input, inputFormat).format(format),
    number: (input) => {
        const parts = String(input).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return parts.join('.');
    },
    trim: (input) => (typeof input === 'string' ? input.trim() : input),
    percent: (input, decimals = 0) => {
        input = parseFloat(input) || 0;
        decimals = parseInt(decimals, 10) || 0;
        const whole = input * 100;
        const multiplier = Math.pow(10, decimals);
        return ((Math.round(whole * multiplier) / multiplier).toFixed(decimals) + '%');
    },
    duration: (input) => (input ? formatDuration(input) : input),
    bytes: (input) => (input ? prettyBytes(parseFloat(input)) : input),
    round: (input, decimals = 2) => {
        if (isNaN(input)) {
            return 0;
        }
        decimals = parseInt(decimals, 10) || 2;
        const multiplier = Math.pow(10, decimals);
        return (Math.round(input * multiplier) / multiplier).toFixed(decimals);
    },
    truncate: (input, length, end) => {
        if (typeof input !== 'string') {
            return input;
        }
        end = end || '...';
        if (length == null) {
            return input;
        }
        length = parseInt(length, 10) || 200;
        return input.substring(0, length) + (input.length > length ? end : '');
    },
    urlEncode: (input) => encodeURIComponent(input),
    urlDecode: (input) => decodeURIComponent(input),
    default: (input, defaultValue) => input ||
        (() => {
            try {
                if (defaultValue === 'undefined') {
                    return undefined;
                }
                return JSON.parse(defaultValue);
            }
            catch (e) {
                return defaultValue;
            }
        })(),
    join: (input, glue) => (input && input.join ? input.join(glue) : input),
    split: (input, delimiter = ',') => typeof input === 'string' ? input.split(delimiter) : input,
    sortBy: (input, key, method = 'alpha', order) => Array.isArray(input) ? input.sort(makeSorter(key, method, order)) : input,
    unique: (input, key) => Array.isArray(input) ? (key ? uniqBy(input, key) : uniq(input)) : input,
    topAndOther: (input, len = 10, labelField = 'name', restLabel = '其他') => {
        if (Array.isArray(input) && len) {
            const grouped = groupBy(input, (item) => {
                const index = input.indexOf(item) + 1;
                return index >= len ? len : index;
            });
            return Object.keys(grouped).map((key, index) => {
                const group = grouped[key];
                const obj = group.reduce((obj, item) => {
                    Object.keys(item).forEach((key) => {
                        if (!has(obj, key) || key === 'labelField') {
                            obj[key] = item[key];
                        }
                        else if (typeof item[key] === 'number' &&
                            typeof obj[key] === 'number') {
                            obj[key] += item[key];
                        }
                        else if (typeof item[key] === 'string' &&
                            /^(?:\-|\.)\d/.test(item[key]) &&
                            typeof obj[key] === 'number') {
                            obj[key] += parseFloat(item[key]) || 0;
                        }
                        else if (typeof item[key] === 'string' &&
                            typeof obj[key] === 'string') {
                            obj[key] += `, ${item[key]}`;
                        }
                        else {
                            obj[key] = item[key];
                        }
                    });
                    return obj;
                }, {});
                if (index === len - 1) {
                    obj[labelField] = restLabel || '其他';
                }
                return obj;
            });
        }
        return input;
    },
    first: (input) => input && input[0],
    nth: (input, nth = 0) => input && input[nth],
    last: (input) => input && (input.length ? input[input.length - 1] : null),
    minus: (input, step = 1) => (parseInt(input, 10) || 0) - parseInt(step, 10),
    plus: (input, step = 1) => (parseInt(input, 10) || 0) + parseInt(step, 10),
    count: (input) => Array.isArray(input) || typeof input === 'string' ? input.length : 0,
    sum: (input, field) => Array.isArray(input)
        ? input.reduce((sum, item) => sum + (parseFloat(field ? pickValues(field, item) : item) || 0), 0)
        : input,
    abs: (input) => (typeof input === 'number' ? Math.abs(input) : input),
    pick: (input, path = '&') => Array.isArray(input) && !/^\d+$/.test(path)
        ? input.map((item, index) => pickValues(path, createObject({ index }, item)))
        : pickValues(path, input),
    pickIfExist: (input, path = '&') => Array.isArray(input)
        ? input.map((item) => resolveVariable(path, item) || item)
        : resolveVariable(path, input) || input,
    str2date: function (input, inputFormat = 'X', outputFormat = 'X') {
        return input
            ? filterDate(input, this, inputFormat).format(outputFormat)
            : '';
    },
    asArray: (input) => (Array.isArray(input) ? input : input ? [input] : input),
    concat(input, ...args) {
        return Array.isArray(input)
            ? input.concat(...args.map((arg) => getStrOrVariable(arg, this)))
            : input;
    },
    filter: function (input, keys, expOrDirective, arg1) {
        if (!Array.isArray(input) || !keys || !expOrDirective) {
            return input;
        }
        let directive = expOrDirective;
        let fn = () => true;
        if (directive === 'isTrue') {
            fn = (value) => !!value;
        }
        else if (directive === 'isFalse') {
            fn = (value) => !value;
        }
        else if (directive === 'isExists') {
            fn = (value) => typeof value !== 'undefined';
        }
        else if (directive === 'equals' || directive === 'equal') {
            arg1 = arg1 ? getStrOrVariable(arg1, this) : '';
            fn = (value) => arg1 == value;
        }
        else if (directive === 'isIn') {
            let list = arg1 ? getStrOrVariable(arg1, this) : [];
            list = str2array(list);
            list = Array.isArray(list) ? list : list ? [list] : [];
            fn = (value) => (list.length ? !!~list.indexOf(value) : true);
        }
        else if (directive === 'notIn') {
            let list = arg1 ? getStrOrVariable(arg1, this) : [];
            list = str2array(list);
            list = Array.isArray(list) ? list : list ? [list] : [];
            fn = (value) => !~list.indexOf(value);
        }
        else {
            if (directive !== 'match') {
                directive = 'match';
                arg1 = expOrDirective;
            }
            arg1 = arg1 ? getStrOrVariable(arg1, this) : '';
            // 比对的值是空时直接返回。
            if (!arg1) {
                return input;
            }
            const reg = string2regExp(arg1, false);
            fn = (value) => reg.test(String(value));
        }
        keys = keys.split(/\s*,\s*/);
        return input.filter((item) => keys.some((key) => fn(resolveVariable(key, item), key, item)));
    },
    base64Encode(str) {
        return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function toSolidBytes(match, p1) {
            return String.fromCharCode(('0x' + p1));
        }));
    },
    base64Decode(str) {
        return decodeURIComponent(atob(str)
            .split('')
            .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
            .join(''));
    },
    lowerCase: (input) => input && typeof input === 'string' ? input.toLowerCase() : input,
    upperCase: (input) => input && typeof input === 'string' ? input.toUpperCase() : input,
    isTrue(input, trueValue, falseValue) {
        return getConditionValue(input, !!input, trueValue, falseValue, this);
    },
    isFalse(input, trueValue, falseValue) {
        return getConditionValue(input, !input, trueValue, falseValue, this);
    },
    isMatch(input, matchArg, trueValue, falseValue) {
        matchArg = getStrOrVariable(matchArg, this);
        return getConditionValue(input, matchArg && string2regExp(matchArg, false).test(String(input)), trueValue, falseValue, this);
    },
    notMatch(input, matchArg, trueValue, falseValue) {
        matchArg = getStrOrVariable(matchArg, this);
        return getConditionValue(input, matchArg && !string2regExp(matchArg, false).test(String(input)), trueValue, falseValue, this);
    },
    isEquals(input, equalsValue, trueValue, falseValue) {
        equalsValue = /^\d+$/.test(equalsValue)
            ? parseInt(equalsValue, 10)
            : getStrOrVariable(equalsValue, this);
        return getConditionValue(input, input === equalsValue, trueValue, falseValue, this);
    },
    notEquals(input, equalsValue, trueValue, falseValue) {
        equalsValue = /^\d+$/.test(equalsValue)
            ? parseInt(equalsValue, 10)
            : getStrOrVariable(equalsValue, this);
        return getConditionValue(input, input !== equalsValue, trueValue, falseValue, this);
    },
};
/**
 * 如果当前传入字符为：'xxx'或者"xxx"，则返回字符xxx
 * 否则去数据域中，获取变量xxx
 *
 * @param value 传入字符
 * @param data 数据域
 */
function getStrOrVariable(value, data) {
    return /^('|")(.*)\1$/.test(value)
        ? RegExp.$2
        : /^-?\d+$/.test(value)
            ? parseInt(value, 10)
            : /^(-?\d+)\.\d+?$/.test(value)
                ? parseFloat(value)
                : /^\[.*\]$/.test(value)
                    ? value
                        .substring(1, value.length - 1)
                        .split(/\s*,\s*/)
                        .filter((item) => item)
                    : /,/.test(value)
                        ? value.split(/\s*,\s*/).filter((item) => item)
                        : resolveVariable(value, data);
}
function str2array(list) {
    if (list && typeof list === 'string') {
        if (/^\[.*\]$/.test(list)) {
            return list
                .substring(1, list.length - 1)
                .split(/\s*,\s*/)
                .filter((item) => item);
        }
        else {
            return list.split(/\s*,\s*/).filter((item) => item);
        }
    }
    return list;
}
function getConditionValue(input, isTrue, trueValue, falseValue, data) {
    return isTrue || (!isTrue && falseValue)
        ? getStrOrVariable(isTrue ? trueValue : falseValue, data)
        : input;
}
function registerFilter(name, fn) {
    filters[name] = fn;
}
function pickValues(names, data) {
    let arr;
    if (!names || ((arr = names.split(',')) && arr.length < 2)) {
        const idx = names.indexOf('~');
        if (~idx) {
            const key = names.substring(0, idx);
            const target = names.substring(idx + 1);
            return {
                [key]: resolveVariable(target, data),
            };
        }
        return resolveVariable(names, data);
    }
    const ret = {};
    arr.forEach((name) => {
        const idx = name.indexOf('~');
        let target = name;
        if (~idx) {
            target = name.substring(idx + 1);
            name = name.substring(0, idx);
        }
        setVariable(ret, name, resolveVariable(target, data));
    });
    return ret;
}
const resolveVariable = (path, data = {}) => {
    if (!path || !data) {
        return undefined;
    }
    if (path === '$$') {
        return data;
    }
    else if (path[0] === '$') {
        path = path.substring(1);
    }
    else if (path === '&') {
        return data;
    }
    return get(data, path);
};
const isPureVariable = (path) => typeof path === 'string'
    ? /^\$(?:([a-z0-9_.]+)|{[^}{]+})$/.test(path)
    : false;
const resolveVariableAndFilter = (path, data = {}, defaultFilter = '| html') => {
    if (!path) {
        return undefined;
    }
    const m = /^(\\)?\$(?:([a-z0-9_.]+)|{([\s\S]+)})$/i.exec(path);
    if (!m) {
        return undefined;
    }
    const [_, escape, key, key2] = m;
    // 如果是转义如： `\$abc` => `$abc`
    if (escape) {
        return _.substring(1);
    }
    let finalKey = key || key2;
    // 先只支持一层吧
    finalKey = finalKey.replace(/(\\|\\\$)?\$(?:([a-zA-Z0-9_.]+)|{([^}{]+)})/g, (_, escape) => {
        return escape
            ? _.substring(1)
            : resolveVariableAndFilter(_, data, defaultFilter);
    });
    // 默认 html 转义
    if (!finalKey.includes('|')) {
        finalKey += defaultFilter;
    }
    const paths = finalKey.split(/\s*\|\s*/g);
    const originalKey = finalKey;
    finalKey = paths.shift();
    const ret = resolveVariable(finalKey, data);
    let prevConInputChanged = false; // 前一个类三元过滤器生效，则跳过后续类三元过滤器
    return ret == null &&
        !originalKey.includes('default') &&
        !originalKey.includes('now')
        ? ret
        : paths.reduce((input, filter) => {
            const params = filter
                .replace(/([^\\])\\([\:\\])/g, (_, affix, content) => `${affix}__${content === ':' ? 'colon' : 'slash'}__`)
                .split(':')
                .map((item) => item.replace(/__(slash|colon)__/g, (_, type) => type === 'colon' ? ':' : '\\'));
            const key = params.shift();
            if ([
                'isTrue',
                'isFalse',
                'isMatch',
                'isEquals',
                'notMatch',
                'notEquals',
            ].includes(key)) {
                if (prevConInputChanged) {
                    return input;
                }
                else {
                    const result = filters[key].call(data, input, ...params);
                    prevConInputChanged = result !== input;
                    return result;
                }
            }
            else {
                // 后面再遇到非类三元filter就重置了吧，不影响再后面的其他三元filter
                prevConInputChanged = false;
            }
            return (filters[key] || filters.raw).call(data, input, ...params);
        }, ret);
};
const tokenize = (str, data, defaultFilter = '| raw') => {
    return str.replace(/(\\)?\$(?:([a-z0-9_\.]+|&|\$)|{([^}{]+?)})/gi, (_, escape, key1, key2, index, source) => {
        if (!escape && key1 === '$') {
            const prefix = source[index - 1];
            return prefix === '='
                ? encodeURIComponent(JSON.stringify(data))
                : qsstringify(data);
        }
        return escape
            ? _.substring(1)
            : resolveVariableAndFilter(_, data, defaultFilter) || '';
    });
};
function resolveMapping(value, data, defaultFilter = '| raw') {
    return typeof value === 'string' && isPureVariable(value)
        ? resolveVariableAndFilter(value, data, defaultFilter)
        : typeof value === 'string' && value.includes('$')
            ? tokenize(value, data, defaultFilter)
            : value;
}
function dataMapping(to, from, ignoreFunction = false) {
    let ret = {};
    if (Array.isArray(to)) {
        return to.map((item) => dataMapping(item, from, ignoreFunction));
    }
    else if (!to) {
        return ret;
    }
    Object.keys(to).forEach((key) => {
        const value = to[key];
        let keys;
        if (typeof ignoreFunction === 'function' && ignoreFunction(key, value)) {
            // 如果被ignore，不做数据映射处理。
            ret[key] = value;
        }
        else if (key === '&' && value === '$$') {
            ret = {
                ...ret,
                ...from,
            };
        }
        else if (key.includes('$') && typeof value === 'string') {
            ret[key.substring(1)] = createStr2Function(value, from);
        }
        else if (key === '&') {
            const v = isPlainObject(value) &&
                (keys = Object.keys(value)) &&
                keys.length === 1 &&
                from[keys[0].substring(1)] &&
                Array.isArray(from[keys[0].substring(1)])
                ? from[keys[0].substring(1)].map((raw) => dataMapping(value[keys[0]], createObject(from, raw), ignoreFunction))
                : resolveMapping(value, from);
            if (Array.isArray(v) || typeof v === 'string') {
                ret = v;
            }
            else if (typeof v === 'function') {
                ret = {
                    ...ret,
                    ...v(from),
                };
            }
            else {
                ret = {
                    ...ret,
                    ...v,
                };
            }
        }
        else if (value === '$$') {
            ret[key] = from;
        }
        else if (value && value[0] === '$') {
            const v = resolveMapping(value, from);
            ret[key] = v;
            if (v === '__undefined') {
                delete ret[key];
            }
        }
        else if (value && value[0] === '&') {
            ret[key] = evalExpression(/\&{([^}{]+)}/g.exec(value)[1], from);
        }
        else if (isPlainObject(value) &&
            (keys = Object.keys(value)) &&
            keys.length === 1 &&
            from[keys[0].substring(1)] &&
            Array.isArray(from[keys[0].substring(1)])) {
            const arr = from[keys[0].substring(1)];
            const mapping = value[keys[0]];
            ret[key] = arr.map((raw) => dataMapping(mapping, createObject(from, raw), ignoreFunction));
        }
        else if (isPlainObject(value)) {
            ret[key] = dataMapping(value, from, ignoreFunction);
        }
        else if (Array.isArray(value)) {
            ret[key] = value.map((value) => isPlainObject(value)
                ? dataMapping(value, from, ignoreFunction)
                : resolveMapping(value, from));
        }
        else if (typeof value == 'string' && value.includes('$')) {
            ret[key] = resolveMapping(value, from);
        }
        else if (typeof value === 'function' && ignoreFunction !== true) {
            ret[key] = value(from);
        }
        else {
            ret[key] = value;
            if (value === '__undefined') {
                delete ret[key];
            }
        }
    });
    return ret;
}
function getFilters() {
    return filters;
}
function registerBuiltin() {
    return {
        name: 'builtin',
        test: (str) => !!str.includes('$'),
        compile: (str, data, defaultFilter = '| html') => tokenize(str, data, defaultFilter),
    };
}

const LIFE_CYCLE = {
    init: 'init',
    didMount: 'didMount',
    willUnmount: 'willUnmount',
    willUpdate: 'willUpdate',
    didUpdate: 'didUpdate',
};

/**
 * 代码来源于amis
 * https://github.com/baidu/amis
 */
const rSchema = /(?:^|raw\:)(get|post|put|delete|patch|options|head):/i;
const apiCaches = [];
function normalizeApi(api, defaultMethod) {
    if (typeof api === 'string') {
        const method = rSchema.test(api) ? RegExp.$1 : '';
        method && (api = api.replace(method + ':', ''));
        api = {
            method: (method || defaultMethod),
            url: api,
        };
    }
    return api;
}
function buildApi(api, data, options = {}) {
    api = normalizeApi(api, options.method);
    const { autoAppend, ignoreData, ...rest } = options;
    api.config = {
        ...rest,
    };
    api.method = (api.method || options.method || 'get').toLowerCase();
    if (!data) {
        return api;
    }
    else if (data instanceof FormData ||
        data instanceof Blob ||
        data instanceof ArrayBuffer) {
        api.data = data;
        return api;
    }
    const raw = api.url || '';
    const idx = api.url.indexOf('?');
    if (~idx) {
        const hashIdx = api.url.indexOf('#');
        const params = qs.parse(api.url.substring(idx + 1, ~hashIdx ? hashIdx : undefined));
        api.url =
            tokenize(api.url.substring(0, idx + 1), data, '| urlEncode') +
                qsstringify((api.query = dataMapping(params, data))) +
                (~hashIdx ? api.url.substring(hashIdx) : '');
    }
    else {
        api.url = tokenize(api.url, data, '| urlEncode');
    }
    if (ignoreData) {
        return api;
    }
    if (api.data) {
        api.body = api.data = dataMapping(api.data, data);
    }
    else if (api.method === 'post' || api.method === 'put') {
        api.body = api.data = cloneObject(data);
    }
    // get 类请求，把 data 附带到 url 上。
    if (api.method === 'get') {
        if (!raw.includes('$') && !api.data && autoAppend) {
            api.query = api.data = data;
        }
        else if (api.attachDataToQuery === false &&
            api.data &&
            !raw.includes('$') &&
            autoAppend) {
            const idx = api.url.indexOf('?');
            if (~idx) {
                const params = (api.query = {
                    ...qs.parse(api.url.substring(idx + 1)),
                    ...data,
                });
                api.url = api.url.substring(0, idx) + '?' + qsstringify(params);
            }
            else {
                api.query = data;
                api.url += '?' + qsstringify(data);
            }
        }
        if (api.data && api.attachDataToQuery !== false) {
            const idx = api.url.indexOf('?');
            if (~idx) {
                const params = (api.query = {
                    ...qs.parse(api.url.substring(idx + 1)),
                    ...api.data,
                });
                api.url = api.url.substring(0, idx) + '?' + qsstringify(params);
            }
            else {
                api.query = api.data;
                api.url += '?' + qsstringify(api.data);
            }
            delete api.data;
        }
    }
    if (api.headers) {
        api.headers = dataMapping(api.headers, data);
    }
    if (api.requestAdaptor && typeof api.requestAdaptor === 'string') {
        api.requestAdaptor = str2function(api.requestAdaptor, 'api');
    }
    if (api.adaptor && typeof api.adaptor === 'string') {
        api.adaptor = str2function(api.adaptor, 'payload', 'response', 'api');
    }
    return api;
}
function str2function(contents, ...args) {
    try {
        const fn = new Function(...args, contents);
        return fn;
    }
    catch (e) {
        console.warn(e);
        return null;
    }
}
function responseAdaptor(ret, api) {
    let hasStatusField = true;
    if (!ret) {
        throw new Error('Response is empty!');
    }
    else if (!has(ret, 'status')) {
        hasStatusField = false;
    }
    const result = ret.data || ret.result;
    const payload = {
        ok: hasStatusField === false || ret.status == 0,
        status: hasStatusField === false ? 0 : ret.status,
        msg: ret.msg || ret.message,
        msgTimeout: ret.msgTimeout,
        data: Array.isArray(result) ? { items: result } : result,
        isNotState: api.isNotState,
        isPageState: api.isPageState,
    };
    if (payload.status == 422) {
        payload.errors = ret.errors;
    }
    if (payload.ok && api.responseData) {
        payload.data = dataMapping(api.responseData, createObject({ api }, payload.data || {}));
    }
    return payload;
}
const defaultFetcher = async (api) => {
    const { url, ...rest } = api;
    const result = await fetch(url, rest);
    return result.json();
};
function wrapFetcher(fn = defaultFetcher) {
    return async function (api, data, options) {
        api = buildApi(api, data, options);
        api.requestAdaptor && (api = api.requestAdaptor(api));
        if (api.data && (hasFile(api.data) || api.dataType === 'form-data')) {
            api.data = object2formData(api.data, api.qsOptions);
        }
        else if (api.data &&
            typeof api.data !== 'string' &&
            api.dataType === 'form') {
            api.data = qsstringify(api.data, api.qsOptions);
            api.headers = api.headers || (api.headers = {});
            api.headers['Content-Type'] = 'application/x-www-form-urlencoded';
        }
        else if (api.data &&
            typeof api.data !== 'string' &&
            api.dataType === 'json') {
            api.data = JSON.stringify(api.data);
            api.headers = api.headers || (api.headers = {});
            api.headers['Content-Type'] = 'application/json';
        }
        const result = await fn(api);
        if (typeof api.cache === 'number' && api.cache > 0) {
            const apiCache = getApiCache(api);
            return wrapAdaptor(apiCache
                ? apiCache.result
                : setApiCache(api, result), api);
        }
        return wrapAdaptor(result, api);
    };
}
async function wrapAdaptor(result, api) {
    const adaptor = api.adaptor;
    return responseAdaptor(adaptor ? adaptor(result, api) : result, api);
}
function isApiOutdated(prevApi, nextApi, prevData, nextData, isFirst) {
    const nextUrl = get(nextApi, 'url', nextApi);
    const prevUrl = get(prevApi, 'url', prevApi);
    if (nextUrl !== prevUrl && isValidApi(nextUrl))
        return true;
    if (!isFirst && get(nextApi, 'autoRefresh') === false) {
        return false;
    }
    if (typeof nextUrl === 'string' && nextUrl.includes('$')) {
        prevApi = buildApi(prevApi, prevData, {
            ignoreData: true,
        });
        nextApi = buildApi(nextApi, nextData, {
            ignoreData: true,
        });
        return !!(prevApi.url !== nextApi.url &&
            isValidApi(nextApi.url) &&
            (!nextApi.sendOn || evalExpression(nextApi.sendOn, nextData)));
    }
    return false;
}
function isValidApi(api) {
    return (api &&
        /^(?:(https?|wss?|taf):\/\/[^\/]+)?(\/?[^\s\/\?]*){1,}(\?.*)?$/.test(api));
}
function isEffectiveApi(api, data, initFetch, initFetchOn) {
    if (!api) {
        return false;
    }
    if (initFetch === false) {
        return false;
    }
    if (initFetchOn && data && !evalExpression(initFetchOn, data)) {
        return false;
    }
    if (typeof api === 'string' && api.length) {
        return true;
    }
    else if (isObject(api) && api.url) {
        if (api.sendOn &&
            data &&
            !evalExpression(api.sendOn, data)) {
            return false;
        }
        return true;
    }
    return false;
}
function isSameApi(apiA, apiB) {
    return (apiA.method === apiB.method &&
        apiA.url === apiB.url &&
        !isObjectShallowModified(apiA.data, apiB.data, false));
}
function getApiCache(api) {
    // 清理过期cache
    const now = Date.now();
    let result;
    for (let idx = 0, len = apiCaches.length; idx < len; idx++) {
        const apiCache = apiCaches[idx];
        if (now - apiCache.requestTime > apiCache.cache) {
            apiCaches.splice(idx, 1);
            len--;
            idx--;
            continue;
        }
        if (isSameApi(api, apiCache)) {
            result = apiCache;
            break;
        }
    }
    return result;
}
function setApiCache(api, result) {
    apiCaches.push({
        ...api,
        result,
        requestTime: Date.now(),
    });
    return result;
}
const handleResponseState = (response) => {
    const state = {};
    const { isPageState, isNotState, data, status } = response;
    if (!isNotState && status === 0) {
        if (isPageState) {
            merge(state, { pageState: data });
        }
        else {
            merge(state, { state: data });
        }
    }
    return state;
};
const fetchData = async (fetcher, prevApi, nextApi, prevData, nextData, isFirst, options) => {
    if (fetcher && nextApi) {
        if (isArray(nextApi)) {
            const stateArr = await Promise.allSettled(map(nextApi, (api, i) => {
                if (isApiOutdated(get(prevApi, i), api, prevData, nextData, isFirst)) {
                    return wrapFetcher(fetcher)(api, nextData, options);
                }
            }));
            for (const result of stateArr) {
                if (get(result, 'value')) {
                    return handleResponseState(get(result, 'value'));
                }
            }
        }
        else {
            if (isApiOutdated(prevApi, nextApi, prevData, nextData, isFirst)) {
                const result = await wrapFetcher(fetcher)(nextApi, nextData, options);
                if (result) {
                    return handleResponseState(result);
                }
            }
        }
    }
};
function clearApiCache() {
    apiCaches.splice(0, apiCaches.length);
}
// window.apiCaches = apiCaches;

const ALL_PROPS = '$all';
function getStateFields(data, fieldsRet) {
    const ret = fieldsRet || [];
    if (Array.isArray(data)) {
        return data.map((item) => getStateFields(item));
    }
    else if (!data) {
        return ret;
    }
    each(data, (value, key) => {
        if (value === '$$' || value === '&') {
            ret.push(ALL_PROPS);
        }
        else if (key !== '$' && key.includes('$')) {
            //todo 字符串方法体
            resolveFieldFromExpression(value, ret);
        }
        else if (typeof value === 'string' &&
            /(\\)?\$(?:([a-z0-9_\.]+|&|\$)|{([^}{]+?)})/gi.test(value)) {
            value.replace(/(\\)?\$(?:([a-z0-9_\.]+|&|\$)|{([^}{]+?)})/gi, (_, escape) => {
                !escape && resolveVariableAndFilterField(_, ret);
                return '';
            });
        }
        else if (isPlainObject(value)) {
            getStateFields(value, ret);
        }
    });
    return [...new Set(ret.filter((field) => field !== 'funParams'))];
}
function getChildrenFields(pageConfig, childNodes) {
    const resultFields = [];
    const getFields = (childNodes) => {
        each(childNodes, (nodeKey) => {
            const { loop } = get(pageConfig, [nodeKey]) || {};
            if (typeof loop === 'string') {
                resultFields.push(...getStateFields({ loop }));
            }
        });
    };
    if (childNodes) {
        if (Array.isArray(childNodes)) {
            getFields(childNodes);
        }
        else {
            each(childNodes, (nodeKeys) => {
                getFields(nodeKeys);
            });
        }
    }
    return resultFields;
}
const resolveFieldFromExpression = (expression, fieldsRet) => {
    expression.replace(/this\.([a-zA-Z0-9]*)/gi, (b, field) => {
        fieldsRet.push(field);
        return '';
    });
};
const resolveVariableAndFilterField = (path, fieldsRet, defaultFilter = '| html') => {
    if (!path) {
        return;
    }
    const m = /^(\\)?\$(?:([a-z0-9_.]+)|{([\s\S]+)})$/i.exec(path);
    if (!m) {
        return;
    }
    const [, escape, key, key2] = m;
    if (escape) {
        return;
    }
    let finalKey = key || key2;
    finalKey = finalKey.replace(/(\\|\\\$)?\$(?:([a-zA-Z0-9_.]+)|{([^}{]+)})/g, (_, escape) => {
        !escape && resolveVariableAndFilterField(_, fieldsRet, defaultFilter);
        return '';
    });
    const paths = finalKey.split(/\s*\|\s*/g);
    finalKey = paths.shift();
    fieldsRet.push(finalKey.split('.').shift());
    return paths.map((filter) => {
        const params = filter
            .replace(/([^\\])\\([\:\\])/g, (_, affix, content) => `${affix}__${content === ':' ? 'colon' : 'slash'}__`)
            .split(':')
            .map((item) => item.replace(/__(slash|colon)__/g, (_, type) => type === 'colon' ? ':' : '\\'));
        const key = params.shift();
        if ([
            'isTrue',
            'isFalse',
            'isMatch',
            'isEquals',
            'notMatch',
            'notEquals',
        ].includes(key)) {
            each(params, (param) => {
                const filed = getStrOrField(param);
                if (filed) {
                    fieldsRet.push(filed.split('.').shift());
                }
            });
            // 后面再遇到非类三元filter就重置了吧，不影响再后面的其他三元filter
        }
    });
};
function getStrOrField(value) {
    return /(^('|")(.*)\1$)|(^-?\d+$)|(^(-?\d+)\.\d+?$)|(^\[.*\]$)|(,)/.test(value)
        ? false
        : value;
}

function userAgent(pattern) {
    if (typeof window !== 'undefined' && window.navigator) {
        return !!( /*@__PURE__*/navigator.userAgent.match(pattern));
    }
}
const IE11OrLess = userAgent(/(?:Trident.*rv[ :]?11\.|msie|iemobile|Windows Phone)/i);
const Edge = userAgent(/Edge/i);
const FireFox = userAgent(/firefox/i);
const Safari = userAgent(/safari/i) && !userAgent(/chrome/i) && !userAgent(/android/i);
const IOS = userAgent(/iP(ad|od|hone)/i);
const ChromeForAndroid = userAgent(/chrome/i) && userAgent(/android/i);

export { ALL_PROPS, ChromeForAndroid, Edge, FUN_EVAL_CACHE, FireFox, IE11OrLess, IOS, LIFE_CYCLE, Safari, buildApi, clearApiCache, createStr2Function, dataMapping, defaultFetcher, escapeHtml, evalExpression, evalJS, fetchData, filter, filterDate, filters, formatDuration, getApiCache, getChildrenFields, getFilters, getStateFields, handleResponseState, isApiOutdated, isEffectiveApi, isPureVariable, isSameApi, isValidApi, normalizeApi, parseDuration, pickValues, prettyBytes, registerBuiltin, registerFilter, registerLodash, registerTplEnginer, relativeValueRe, resolveFieldFromExpression, resolveMapping, resolveVariable, resolveVariableAndFilter, resolveVariableAndFilterField, setApiCache, setCustomEvalExpression, setCustomEvalJs, tokenize, wrapAdaptor, wrapFetcher };
